import { LengthCounterTable } from './LengthCounterTable';

export class PulseChannel implements ISerializable
{
    // The 4 different waveforms (Duty Cycles) a Pulse channel can produce
    private static readonly DUTY_TABLE = [
        [0, 1, 0, 0, 0, 0, 0, 0], // 12.5%
        [0, 1, 1, 0, 0, 0, 0, 0], // 25%
        [0, 1, 1, 1, 1, 0, 0, 0], // 50%
        [1, 0, 0, 1, 1, 1, 1, 1], // 25% negated
    ];

    private isEnabled: boolean = false;
    public lengthCounter: number = 0;

    // Registers
    private dutyMode: number = 0;
    private timerPeriod: number = 0;

    // Envelope / Volume
    private envelopeStart: boolean = false;
    private envelopeLoop: boolean = false;
    private constantVolumeFlag: boolean = false;
    private envelopeVolume: number = 0;
    private constantVolume: number = 0;
    private envelopeDivider: number = 0;

    // Sweep Unit
    private sweepEnabled: boolean      = false;
    private sweepPeriod: number        = 0;
    private sweepNegate: boolean       = false;
    private sweepShift: number         = 0;
    private sweepReload: boolean       = false;
    private sweepDivider: number       = 0;
    private targetPeriod: number       = 0;

    // Internal Counters
    private timerValue: number = 0;
    private sequenceIndex: number = 0;

    constructor(private readonly channelNumber: 1 | 2) {}

    public writeRegister(address: number, value: number): void
    {
        // Address is usually 0x4000-0x4003 (Pulse 1) or 0x4004-0x4007 (Pulse 2)
        const register = address % 4;

        switch (register) {
            case 0:
                // $4000 / $4004: Volume, Envelope, and Duty Cycle
                this.dutyMode           = (value >> 6) & 0x03;
                this.envelopeLoop       = ((value >> 5) & 1) === 1;
                this.constantVolumeFlag = ((value >> 4) & 1) === 1;
                this.constantVolume     = value & 0x0F;
                break;
            case 1:
                // $4001 / $4005: Sweep Unit
                this.sweepEnabled = ((value >> 7) & 1) === 1;
                this.sweepPeriod  = (value >> 4) & 0x07;
                this.sweepNegate  = ((value >> 3) & 1) === 1;
                this.sweepShift   = value & 0x07;
                this.sweepReload  = true;
                this.updateTargetPeriod();
                break;
            case 2:
                // $4002 / $4006: Timer (low 8 bits)
                this.timerPeriod = (this.timerPeriod & 0x0700) | value;
                this.updateTargetPeriod();
                break;
            case 3:
                // $4003 / $4007: Timer (high 3 bits) and Length Counter Load
                this.timerPeriod = (this.timerPeriod & 0x00FF) | ((value & 0x07) << 8);
                this.updateTargetPeriod();

                if (this.isEnabled) {
                    this.lengthCounter = LengthCounterTable[(value >> 3) & 0x1F];
                }

                // Restart the sequence and envelope
                this.sequenceIndex = 0;
                this.envelopeStart = true;
                break;
        }
    }

    public setEnabled(enabled: boolean): void
    {
        this.isEnabled = enabled;
        if (!enabled) {
            this.lengthCounter = 0;
        }
    }

    /**
     * Ticks at HALF the CPU clock rate (every 2 cycles)
     */
    public tickTimer(): void
    {
        if (this.timerValue > 0) {
            this.timerValue--;
        } else {
            this.timerValue = this.timerPeriod;
            // The sequence index counts down from 7 to 0
            this.sequenceIndex = (this.sequenceIndex - 1) & 0x07;
        }
    }

    /**
     * Ticks on the APU Quarter Frame (approx 240Hz/192Hz)
     */
    public clockEnvelope(): void
    {
        if (this.envelopeStart) {
            this.envelopeStart = false;
            this.envelopeVolume = 15;
            this.envelopeDivider = this.constantVolume;
        } else {
            if (this.envelopeDivider > 0) {
                this.envelopeDivider--;
            } else {
                this.envelopeDivider = this.constantVolume;
                if (this.envelopeVolume > 0) {
                    this.envelopeVolume--;
                } else if (this.envelopeLoop) {
                    this.envelopeVolume = 15;
                }
            }
        }
    }

    /**
     * Ticks on the APU Half Frame (approx 120Hz/96Hz)
     */
    public clockLength(): void
    {
        if (!this.envelopeLoop && this.lengthCounter > 0) {
            this.lengthCounter--;
        }
    }

    public clockSweep(): void
    {
        if (this.sweepDivider === 0 && this.sweepEnabled && this.sweepShift > 0 &&
            this.timerPeriod >= 8 && this.targetPeriod <= 0x07FF) {

            this.timerPeriod = this.targetPeriod;
            this.updateTargetPeriod();
        }

        if (this.sweepDivider === 0 || this.sweepReload) {
            this.sweepDivider = this.sweepPeriod;
            this.sweepReload  = false;
        } else {
            this.sweepDivider--;
        }
    }

    public get output(): number
    {
        if (!this.isEnabled ||
            this.lengthCounter === 0 ||
            this.timerPeriod < 8 ||
            this.targetPeriod > 0x07FF
        ) {
            return 0;
        }

        // Check the duty cycle table to see if the wave is currently "high" (1) or "low" (0)
        const sequenceValue = PulseChannel.DUTY_TABLE[this.dutyMode][this.sequenceIndex];

        if (sequenceValue === 0) {
            return 0;
        }

        // Return the current volume (either fixed or envelope-driven)
        return this.constantVolumeFlag ? this.constantVolume : this.envelopeVolume;
    }

    public serialize(): Record<string, any>
    {
        // ... (Serialize all the primitive properties here just like TriangleChannel) ...
        return {};
    }

    public deserialize(data: Record<string, any>): void
    {
        // ... (Deserialize all the primitive properties here) ...
    }

    private updateTargetPeriod(): void
    {
        let change = this.timerPeriod >> this.sweepShift;

        if (this.sweepNegate) {
            change = -change;
            // The famous hardware bug! Pulse 1 uses one's complement (subtracts an extra 1).
            // Pulse 2 uses two's complement (subtracts perfectly).
            if (this.channelNumber === 1) {
                change--;
            }
        }

        this.targetPeriod = this.timerPeriod + change;
    }
}
